import { notifications } from "@mantine/notifications"
import { nprogress } from "@mantine/nprogress"
import {
  UseMutationOptions,
  keepPreviousData,
  useMutation,
  useQuery,
} from "@tanstack/react-query"
import { AxiosError } from "axios"
import { format } from "date-fns"
import i18next, { TFunction } from "i18next"
import { useCallback } from "react"
import { useTranslation } from "react-i18next"

import { LibraryResponses } from "@tfg/types/endpoints/library"
import { ProductResponses } from "@tfg/types/endpoints/products"
import {
  bulkLaunchEcobalyseAnalysisType,
  bulkUpdateAgecShowValidationType,
  getProductsExportValidationType,
} from "@tfg/types/schemas/products"

import { httpClient } from "~/backend/axios.ts"
import { queryClient } from "~/backend/queryClient.ts"
import { CursorBasedPagination } from "~/components/hooks/usePagination"
import {
  findCatalogProductsQueryKey,
  FindProductsCatalogRequestFiltersDto,
  FindProductsCatalogRequestSortsDto,
  FindProductsStandardsRequestFiltersDto,
  useDeleteProducts,
  VerifyRawOperationsFileResponseDto,
} from "~/gen/api"
import { showAxiosError } from "~/utils/showAxiosError"
import { showSuccessNotification } from "~/utils/showSuccessNotification"

export const useProductsQuery = ({
  fetchDataOptions,
  filters,
}: {
  fetchDataOptions: CursorBasedPagination
  filters: FindProductsCatalogRequestFiltersDto
}) =>
  useQuery({
    queryKey: ["products", fetchDataOptions, filters],
    queryFn: async () => {
      const res = await httpClient.get<ProductResponses.GetProducts>(
        `products`,
        {
          headers: {
            "accept-language": i18next.language.toUpperCase(),
          },
          params: {
            fetchDataOptions,
            filters,
          },
        }
      )
      return res.data
    },
    placeholderData: keepPreviousData,
  })

export const useAgecComplianceProductsQuery = () =>
  useQuery({
    queryKey: ["products", "agecComplianceProducts"],
    queryFn: async () => {
      const res = await httpClient.get<
        LibraryResponses.AgecComplianceProducts[]
      >(`v1/product/find-agec-compliance`)
      return res.data
    },
  })

export const useBulkUpdateAgecShowMutation = (
  options?: UseMutationOptions<
    unknown,
    unknown,
    bulkUpdateAgecShowValidationType
  >
) => {
  return useMutation<unknown, unknown, bulkUpdateAgecShowValidationType>({
    mutationKey: ["productsUpdate"],

    mutationFn: async (variables) => {
      const response = await httpClient.patch(
        `products/agec-show/bulk-update`,
        variables
      )
      return response.data
    },

    ...options,

    onSuccess: async (data, variables, onMutateResult, context) => {
      await queryClient.invalidateQueries({
        queryKey: ["products"],
      })
      options?.onSuccess?.(data, variables, onMutateResult, context)
    },
  })
}

export const useEcobalyseAnalysisNotifier = () => {
  const notifyOnEcobalyseAnalysisResponse = useCallback(
    (
      data: ProductResponses.EcobalyseIneligibilityErrors,
      t: TFunction<"products", undefined>
    ): void => {
      const { errorStats } = data

      if (errorStats.none > 0) {
        showSuccessNotification({
          title: t("lists.analysis.notifications.success.title"),
          message: t("lists.analysis.notifications.success.message", {
            analysisNumber: errorStats.none,
          }),
        })
      }

      const [, productsWithErrors] = Object.entries(errorStats).reduce(
        ([, acc], [key, value]) => {
          if (key !== "none") {
            acc = acc + value
          }
          return ["errors", acc]
        },
        ["errors", 0]
      )

      if (productsWithErrors > 0) {
        notifications.show({
          color: "red",
          title: t("lists.analysis.notifications.error.title"),
          message: t("lists.analysis.notifications.error.message", {
            analysisNumber: productsWithErrors,
          }),
        })
      }
    },
    []
  )

  return { notifyOnEcobalyseAnalysisResponse }
}

export const useBulkLaunchEcobalyseAnalysisMutation = (
  options?: UseMutationOptions<
    ProductResponses.EcobalyseIneligibilityErrors,
    unknown,
    bulkLaunchEcobalyseAnalysisType
  >
) => {
  return useMutation<
    ProductResponses.EcobalyseIneligibilityErrors,
    unknown,
    bulkLaunchEcobalyseAnalysisType
  >({
    mutationKey: ["productsUpdate"],

    mutationFn: async (variables) => {
      const response = await httpClient.patch(`analysis/ecobalyse`, variables)
      return response.data
    },

    ...options,

    onSuccess: async (data, variables, onMutateResult, context) => {
      await queryClient.invalidateQueries({
        queryKey: ["products"],
      })
      options?.onSuccess?.(data, variables, onMutateResult, context)
    },
  })
}

export const useDownloadProductsCSVMutation = ({
  filters,
}: getProductsExportValidationType) => {
  const { t } = useTranslation("products")
  return useMutation<void, AxiosError>({
    mutationKey: ["products", "download-csv"],

    mutationFn: async () => {
      const response = await httpClient.get("products/export", {
        responseType: "blob",
        headers: {
          "accept-language": i18next.language.toUpperCase(),
        },
        params: {
          filters,
        },
      })

      const filename = `products-${format(new Date(), "yyyy-MM-dd")}.csv`
      const element = document.createElement("a")
      element.href = URL.createObjectURL(new Blob([response.data]))
      element.download = filename

      document.body.appendChild(element)
      element.click()
    },

    onSuccess: () => {
      nprogress.reset()
    },

    onError: (error) => {
      notifications.show({
        title: t("lists.export.error.title"),
        message: t("lists.export.error.message"),
        color: "red",
      })
      console.error("Error downloading products csv", error)
      return error
    },
  })
}

export const useDownloadProductsCertificatesCSVMutation = ({
  filters,
}: {
  filters: FindProductsStandardsRequestFiltersDto
}) => {
  return useMutation<void, AxiosError>({
    mutationKey: ["products", "certificates", "download-csv"],

    mutationFn: async () => {
      const response = await httpClient.get(
        "/v1/product/export-product-certificates",
        {
          responseType: "blob",
          params: {
            filters,
          },
        }
      )

      const element = document.createElement("a")
      element.href = URL.createObjectURL(new Blob([response.data]))
      element.download = "products-certificates.csv"

      document.body.appendChild(element)
      element.click()
    },
  })
}

export const useDownloadProductsLCACSVMutation = () => {
  return useMutation<void, AxiosError>({
    mutationKey: ["products", "download-lca-csv"],

    mutationFn: async () => {
      const response = await httpClient.get("products/lca/export", {
        responseType: "blob",
      })

      // anchor link
      const element = document.createElement("a")
      element.href = URL.createObjectURL(new Blob([response.data]))
      element.download = "products-lca.csv"

      // simulate link click
      document.body.appendChild(element) // Required for this to work in FireFox
      element.click()
    },
  })
}

export const useDownloadProductLCACSVMutation = (productId: number) => {
  return useMutation<void, AxiosError>({
    mutationKey: ["products", productId, "download-lca-csv"],

    mutationFn: async () => {
      const response = await httpClient.get(
        `products/${productId}/export/lca`,
        {
          responseType: "blob",
        }
      )

      // anchor link
      const element = document.createElement("a")
      element.href = URL.createObjectURL(new Blob([response.data]))
      element.download = `product-${productId}-lca.csv`

      // simulate link click
      document.body.appendChild(element) // Required for this to work in FireFox
      element.click()
    },
  })
}

type VerifyRawOperationsFileDto = {
  file: File | null
}

export function useVerifyRawOperationsFileMutation(
  options?: UseMutationOptions<
    VerifyRawOperationsFileResponseDto,
    AxiosError<object | string>,
    VerifyRawOperationsFileDto
  >
) {
  return useMutation<
    VerifyRawOperationsFileResponseDto,
    AxiosError<object | string>,
    VerifyRawOperationsFileDto
  >({
    mutationFn: async ({ file }: VerifyRawOperationsFileDto) => {
      const formData = new FormData()
      if (file) {
        formData.append("file", file)
      }
      const response =
        await httpClient.post<VerifyRawOperationsFileResponseDto>(
          "/v1/product/verify-raw-operations-file",
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        )
      return response.data
    },
    ...options,
  })
}

export type LaunchTraceabilityDto = {
  filePath?: string
  fileOriginalName?: string
}

export function useDeleteProductsMutation({
  filters,
  sorts,
  fetchDataOptions,
  onDeleteSettled,
}: {
  filters: FindProductsCatalogRequestFiltersDto
  sorts: FindProductsCatalogRequestSortsDto
  fetchDataOptions: CursorBasedPagination
  onDeleteSettled: () => void
}) {
  const { t } = useTranslation("products")

  return useDeleteProducts({
    mutation: {
      onSuccess: () => {
        showSuccessNotification({
          title: t("bulkActions.delete.notifications.success.title"),
          message: t("bulkActions.delete.notifications.success.message"),
        })
      },
      onError: (error) => {
        showAxiosError({
          notificationTitle: t("bulkActions.delete.notifications.error.title"),
          notificationMessage: t(
            "bulkActions.delete.notifications.error.message"
          ),
          showNotification: true,
          error,
        })
      },
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: findCatalogProductsQueryKey({
            filters,
            sorts,
            pagination: fetchDataOptions,
          }),
        })
        onDeleteSettled()
      },
    },
  })
}
